import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, tap } from "rxjs";
import { HttpErrorDetail } from "src/app/modals/error-details/error-details-dialog.interface";
import { ErrorNotificationService } from "src/app/services/error-notification.service";
import { FromRouter, RootState } from "src/app/store";
import { SKIP_GLOBAL_ERROR } from "./skip-global-error.context";

interface BackendErrorBody {
  errorType?: unknown;
  errorMessage?: unknown;
  message?: unknown;
}

/**
 * Requests that retry on their own and whose generated client cannot take an HttpContext (SKIP_GLOBAL_ERROR).
 * Error handlers that inform the user themselves call ErrorNotificationService.markHandled() instead.
 */
const SELF_RETRIED_CALLS: { method: string; url: RegExp; param?: string }[] = [
  // Agent chat view long-poll (a plain snapshot hits the same URL without `waitMs` and must still notify).
  { method: "GET", url: /\/ai-platform-service\/chats\/[^/]+\/view$/, param: "waitMs" },
  // Agent chat view stream: it falls back to the long-poll on its own.
  { method: "GET", url: /\/ai-platform-service\/chats\/[^/]+\/view\/stream$/ },
];

/**
 * The agent's chat routes and chat list: the store retries them, and refreshes or re-follows them on its own,
 * so a 503 or a network failure there recovers without the global notification.
 */
const AGENT_RECOVERING_ROUTES = /\/ai-platform-service\/(chats\/|agents\/[^/]+\/chats(?:$|[/?]))/;

/**
 * Notifies the user about server (5xx) and network (status 0) failures.
 * Registered last so it sees the original HttpErrorResponse before other interceptors reshape it.
 */
@Injectable({ providedIn: "root" })
export class ErrorNotificationInterceptor implements HttpInterceptor {
  private readonly errorNotificationService = inject(ErrorNotificationService);
  private readonly router = inject(Router);
  private readonly programId = inject(Store<RootState>).selectSignal(FromRouter.selectProgramId);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.context.get(SKIP_GLOBAL_ERROR) || this.isAssetsRequest(req) || this.isSelfRetriedRequest(req)) return next.handle(req);

    return next.handle(req).pipe(
      tap({
        error: (error: unknown) => {
          if (!(error instanceof HttpErrorResponse)) return;
          if (this.shouldNotify(error) && !this.isAgentRecovery(req, error)) {
            this.errorNotificationService.report(this.toDetail(req, error));
          } else this.errorNotificationService.trackFailure();
        },
      }),
    );
  }

  private shouldNotify(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= HttpStatusCode.InternalServerError.valueOf();
  }

  private isAssetsRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes("/assets/");
  }

  private isSelfRetriedRequest(req: HttpRequest<unknown>): boolean {
    // The query may be in the HttpParams or written into the URL; match the path alone.
    const [path, query = ""] = req.url.split("?");
    const inline = new URLSearchParams(query);
    return SELF_RETRIED_CALLS.some(
      call =>
        call.method === req.method &&
        call.url.test(path) &&
        (call.param === undefined || req.params.has(call.param) || inline.has(call.param)),
    );
  }

  /**
   * Agent failures the agent store recovers from without the user: a 503 that names its Retry-After (the store
   * retries the same request), and a dropped connection on a read (its background refreshes and the view feed
   * try again). Any other agent failure, and every failure elsewhere, still notifies.
   */
  private isAgentRecovery(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    if (!AGENT_RECOVERING_ROUTES.test(req.url)) return false;
    if (error.status === HttpStatusCode.ServiceUnavailable.valueOf()) {
      const body = error.error as { retryAfter?: unknown } | null;
      return error.headers?.has("Retry-After") || typeof body?.retryAfter === "number";
    }
    return error.status === 0 && req.method === "GET";
  }

  private toDetail(req: HttpRequest<unknown>, error: HttpErrorResponse): HttpErrorDetail {
    const body = (typeof error.error === "object" ? error.error : null) as BackendErrorBody | null;
    const asText = (value: unknown): string | undefined => (typeof value === "string" && value ? value : undefined);

    return {
      timestamp: new Date().toISOString(),
      method: req.method,
      url: req.urlWithParams,
      status: error.status,
      statusText: error.statusText,
      errorType: asText(body?.errorType),
      errorMessage: asText(body?.errorMessage) ?? asText(body?.message) ?? error.message,
      interactionId: req.headers.get("interaction-id") ?? undefined,
      programId: this.programId(),
      route: this.router.url,
    };
  }
}
